import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { Button } from './primitives.js';

/* -------------------------------------------------------------------------
   EmptyState

   §29/§30: a page with no data must say so honestly and say what unlocks it.
   `availability` is the important prop — it tells the operator that the
   emptiness is a capability that has not shipped, not a failure or an empty
   search. It takes a finished sentence rather than a milestone name: internal
   scheduling is not a customer-facing fact, and a banner that names one goes
   stale the day it ships.
   ------------------------------------------------------------------------- */

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description: string;
  /** e.g. "VC-2" — the milestone that makes this resource creatable. */
  availability?: string;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  icon,
  title,
  description,
  availability,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={clsx(
        'flex flex-col items-center justify-center px-6 py-14 text-center',
        className,
      )}
    >
      {icon ? (
        <div
          aria-hidden="true"
          className={clsx(
            'mb-4 flex size-11 items-center justify-center rounded-[var(--vc-radius-lg)]',
            'border border-[var(--vc-border)] bg-[var(--vc-surface-sunken)]',
            'text-[var(--vc-text-subtle)] [&>svg]:size-5',
          )}
        >
          {icon}
        </div>
      ) : null}

      <h3 className="text-[14px] font-semibold text-[var(--vc-text)]">{title}</h3>
      <p className="mt-1.5 max-w-[46ch] text-[13px] leading-relaxed text-[var(--vc-text-muted)]">
        {description}
      </p>

      {availability ? (
        <p className="mt-3 text-[12px] text-[var(--vc-text-subtle)]">{availability}</p>
      ) : null}

      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

/* -------------------------------------------------------------------------
   LoadingState / ErrorState
   ------------------------------------------------------------------------- */

export function LoadingState({
  label = 'Loading',
  className,
}: {
  label?: string;
  className?: string;
}) {
  return (
    <div
      className={clsx('flex items-center justify-center gap-2.5 px-6 py-10', className)}
      role="status"
      aria-live="polite"
    >
      <span
        aria-hidden="true"
        className={clsx(
          'size-3.5 animate-spin rounded-full border-2',
          'border-[var(--vc-border-strong)] border-t-[var(--vc-accent)]',
        )}
      />
      <span className="text-[13px] text-[var(--vc-text-muted)]">{label}…</span>
    </div>
  );
}

export interface ErrorStateProps {
  title?: string;
  description: string;
  /** Surfaced verbatim so an operator can quote it in a support request (§22). */
  requestId?: string | null;
  onRetry?: () => void;
  className?: string;
}

export function ErrorState({
  title = 'Something went wrong',
  description,
  requestId,
  onRetry,
  className,
}: ErrorStateProps) {
  return (
    <div
      className={clsx('flex flex-col items-center px-6 py-12 text-center', className)}
      role="alert"
    >
      <div
        aria-hidden="true"
        className={clsx(
          'mb-4 flex size-11 items-center justify-center rounded-[var(--vc-radius-lg)]',
          'border border-[var(--vc-danger-border)] bg-[var(--vc-danger-bg)]',
        )}
      >
        <svg viewBox="0 0 24 24" fill="none" className="size-5 text-[var(--vc-danger-text)]">
          <path
            d="M12 8.5v4.5m0 3.2v.1M10.3 3.9 2.6 17.2A2 2 0 0 0 4.3 20.2h15.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <h3 className="text-[14px] font-semibold text-[var(--vc-text)]">{title}</h3>
      <p className="mt-1.5 max-w-[52ch] text-[13px] leading-relaxed text-[var(--vc-text-muted)]">
        {description}
      </p>

      {requestId ? (
        <p className="mt-3 text-[12px] text-[var(--vc-text-subtle)]">
          Request ID{' '}
          <code className="font-[family-name:var(--vc-font-mono)] text-[var(--vc-text-muted)]">
            {requestId}
          </code>
        </p>
      ) : null}

      {onRetry ? (
        <Button className="mt-5" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}
